import { useEffect, useState } from "react";
import {
  getReminderPrefs, setReminderPrefs, testReminder, getReminderLog,
  getGcalSettings, setGcalSettings, gcalSyncNow, syncMissingGcal, resyncAllGcal, cleanupGcalOrphans,
  getConsultants, setConsultants,
  getIssuer, setIssuer, geocodeIssuer,
  getApiKeys, setApiKeys, weeklyBackupUrl, guideSegreteriaUrl,
  gcalAuthorize, gcalStatus, gcalDisconnect,
} from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Bell, Send, Loader2, CheckCircle2, CalendarSync, RefreshCw, FileSpreadsheet, KeyRound, Archive, Eye, EyeOff, Trash2, Download, Link2, LogOut, AlertTriangle, BookOpen, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [prefs, setPrefs] = useState({ enabled: true, send_to_client_email: true, cc_owner: false, owner_email: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [log, setLog] = useState([]);
  const [gcal, setGcal] = useState({ ics_url: "", enabled: false, last_sync: null, last_count: 0 });
  const [gcalSaving, setGcalSaving] = useState(false);
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [issuer, setIssuerState] = useState({
    denominazione: "", nome: "", cognome: "", piva: "", codice_fiscale: "",
    regime_fiscale: "RF01", codice_ateco: "",
    address: "", cap: "", city: "", provincia: "", nazione: "IT",
    telefono: "", email: "", id_paese_trasmittente: "IT", id_codice_trasmittente: "",
  });
  const [issuerSaving, setIssuerSaving] = useState(false);
  const [apiKeys, setApiKeysState] = useState({ anthropic_api_key: "", resend_api_key: "", resend_from_email: "", has_anthropic: false, has_resend: false });
  const [apiKeysDraft, setApiKeysDraft] = useState({ anthropic_api_key: "", resend_api_key: "", resend_from_email: "" });
  const [apiKeysSaving, setApiKeysSaving] = useState(false);
  const [showAnthropic, setShowAnthropic] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [gcalConn, setGcalConn] = useState(null);

  useEffect(() => {
    getReminderPrefs().then(setPrefs).catch(() => {});
    getReminderLog().then(setLog).catch(() => {});
    getGcalSettings().then(setGcal).catch(() => {});
    getIssuer().then(setIssuerState).catch(() => {});
    getApiKeys().then((d) => {
      setApiKeysState(d);
      setApiKeysDraft({ anthropic_api_key: "", resend_api_key: "", resend_from_email: d.resend_from_email || "" });
    }).catch(() => {});
    gcalStatus().then(setGcalConn).catch(() => {});
  }, []);

  const connectGcal = async () => {
    try {
      const { authorization_url } = await gcalAuthorize();
      const w = window.open(authorization_url, "gcal-oauth", "width=520,height=680");
      if (!w) { toast.error("Popup bloccato dal browser - abilita i popup per questo sito"); return; }
      let done = false;
      const finish = async (ok) => {
        if (done) return; done = true;
        clearInterval(timer);
        window.removeEventListener("message", onMsg);
        try { w?.close(); } catch { /* ignore */ }
        const s = await gcalStatus().catch(() => ({ connected: false }));
        setGcalConn(s);
        if (s.connected) toast.success("Google Calendar connesso");
        else if (ok) toast.warning("Autorizzazione ricevuta ma token non salvato - controlla i log");
        else toast.error("Connessione annullata o fallita - controlla l'email è nei Test users su Google Cloud");
      };
      const onMsg = (ev) => { if (ev?.data?.type === "gcal-connected") finish(true); };
      window.addEventListener("message", onMsg);
      // Poll status every 2s until connected or window closed (timeout 3min)
      const started = Date.now();
      const timer = setInterval(async () => {
        if (done) return;
        if (Date.now() - started > 180000) { finish(false); return; }
        const s = await gcalStatus().catch(() => null);
        if (s?.connected) { finish(true); return; }
        if (w?.closed) { setTimeout(() => finish(false), 1500); }
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore avvio OAuth"); }
  };

  const disconnectGcal = async () => {
    if (!window.confirm("Disconnetti Google Calendar? Gli interventi già pubblicati restano su Google.")) return;
    await gcalDisconnect();
    setGcalConn({ connected: false });
    toast.success("Disconnesso");
  };


  const save = async () => {
    setSaving(true);
    try { await setReminderPrefs(prefs); toast.success("Preferenze salvate"); }
    catch (_e) { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { await testReminder(); toast.success("Email di test inviata al tuo indirizzo"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio fallito"); }
    finally { setTesting(false); }
  };

  const [consultants, setConsultantsState] = useState(1);
  const [consultantsSaving, setConsultantsSaving] = useState(false);
  useEffect(() => { getConsultants().then((r) => setConsultantsState(r.consultants_count || 1)).catch(() => {}); }, []);
  const saveConsultants = async (n) => {
    setConsultantsSaving(true);
    try {
      const r = await setConsultants(n);
      setConsultantsState(r.consultants_count);
      toast.success(`Consulenti disponibili: ${r.consultants_count}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setConsultantsSaving(false); }
  };

  const saveGcal = async () => {
    setGcalSaving(true);
    try {
      const r = await setGcalSettings({ ics_url: gcal.ics_url || "", enabled: !!gcal.enabled });
      setGcal((g) => ({ ...g, ics_url: r.ics_url }));
      toast.success("Impostazioni Google Calendar salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setGcalSaving(false); }
  };

  // Consultants capacity block (rendered inside main return, using state above)
  const ConsultantsBlock = () => (
    <Card className="p-6 border-slate-200 bg-white">
      <h2 className="font-bold font-display text-lg text-slate-900 mb-1">Consulenti disponibili</h2>
      <p className="text-xs text-slate-500 mb-3">Quanti consulenti operativi possono lavorare in parallelo. Se impostato a 2 o pi&ugrave;, PlanOp permette pi&ugrave; interventi sovrapposti nello stesso slot (utile quando si mandano pi&ugrave; consulenti in contemporanea a clienti diversi).</p>
      <div className="flex items-center gap-3">
        <select data-testid="consultants-select" value={consultants} onChange={(e) => saveConsultants(parseInt(e.target.value, 10))} disabled={consultantsSaving}
                className="h-10 w-32 rounded-md border border-slate-300 px-3 text-sm bg-white">
          {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n} {n === 1 ? "consulente" : "consulenti"}</option>)}
        </select>
        {consultantsSaving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
    </Card>
  );

  const syncNow = async () => {
    setGcalSyncing(true);
    try {
      const r = await gcalSyncNow();
      toast.success(`${r.synced} eventi sincronizzati da Google Calendar`);
      const fresh = await getGcalSettings();
      setGcal(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync fallito"); }
    finally { setGcalSyncing(false); }
  };

  const pushMissing = async () => {
    setGcalSyncing(true);
    try {
      const r = await syncMissingGcal();
      if (r.scheduled === 0) toast.success("Tutti gli interventi sono già sincronizzati su Google Calendar");
      else toast.success(`${r.scheduled} interventi in coda per il push su Google Calendar (2-3 sec)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setGcalSyncing(false); }
  };

  const resyncAll = async () => {
    if (!window.confirm("Aggiorna TUTTI gli eventi già presenti su Google Calendar (utile dopo modifica formato titolo). Procedere?")) return;
    setGcalSyncing(true);
    try {
      const r = await resyncAllGcal();
      toast.success(`${r.scheduled} eventi in aggiornamento su Google Calendar (attendi 20-30 sec)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setGcalSyncing(false); }
  };

  const cleanupOrphans = async () => {
    if (!window.confirm(
      "PULIZIA ORFANI Google Calendar\n\n" +
      "Tutti gli interventi PlanOp verranno aggiornati su Google Calendar (quelli mancanti ricreati), " +
      "poi verranno CANCELLATI gli eventi PlanOp duplicati o non più collegati ad un intervento attuale.\n\n" +
      "Utile per rimuovere duplicati/orfani da vecchi test. Procedere?"
    )) return;
    setGcalSyncing(true);
    try {
      await cleanupGcalOrphans();
      toast.success("Riallineamento con Google Calendar avviato: duplicati rimossi e mancanti ricreati (attendi ~1 min)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore pulizia"); }
    finally { setGcalSyncing(false); }
  };

  const saveIssuer = async () => {
    setIssuerSaving(true);
    try { await setIssuer(issuer); toast.success("Dati emittente salvati"); }
    catch (_e) { toast.error("Errore salvataggio"); }
    finally { setIssuerSaving(false); }
  };

  const saveApiKeys = async () => {
    setApiKeysSaving(true);
    try {
      const payload = {
        anthropic_api_key: apiKeysDraft.anthropic_api_key || "",
        resend_api_key: apiKeysDraft.resend_api_key || "",
        resend_from_email: apiKeysDraft.resend_from_email || "",
      };
      const r = await setApiKeys(payload);
      toast.success("Chiavi API salvate");
      const fresh = await getApiKeys();
      setApiKeysState(fresh);
      setApiKeysDraft({ anthropic_api_key: "", resend_api_key: "", resend_from_email: fresh.resend_from_email || "" });
    } catch (_e) { toast.error("Errore salvataggio chiavi"); }
    finally { setApiKeysSaving(false); }
  };

  const clearOneKey = async (which) => {
    try {
      const payload = { anthropic_api_key: "", resend_api_key: "", resend_from_email: apiKeys.resend_from_email || "" };
      payload[which] = "__clear__";
      await setApiKeys(payload);
      toast.success("Chiave rimossa");
      const fresh = await getApiKeys();
      setApiKeysState(fresh);
      setApiKeysDraft({ anthropic_api_key: "", resend_api_key: "", resend_from_email: fresh.resend_from_email || "" });
    } catch (_e) { toast.error("Errore"); }
  };

  return (
    <div data-testid="settings-section" className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="text-xs font-medium tracking-wider uppercase text-slate-500">Configurazione</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold font-display text-slate-900">Impostazioni</h1>
        <p className="mt-2 text-slate-600">Reminder automatici via email 24h prima di ogni intervento.</p>
      </div>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Reminder Email</h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-900">Reminder attivi</div>
              <div className="text-xs text-slate-500">Ogni giorno alle 09:00 (Italia), invia email per gli interventi di domani</div>
            </div>
            <Switch data-testid="reminder-enabled" checked={prefs.enabled} onCheckedChange={(v) => setPrefs({ ...prefs, enabled: v })} />
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-900">Invia al cliente</div>
              <div className="text-xs text-slate-500">Usa l'email presente in anagrafica cliente</div>
            </div>
            <Switch data-testid="reminder-client" checked={prefs.send_to_client_email} onCheckedChange={(v) => setPrefs({ ...prefs, send_to_client_email: v })} />
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-900">Invia una copia a me</div>
              <div className="text-xs text-slate-500">Fallback se il cliente non ha email</div>
            </div>
            <Switch data-testid="reminder-cc" checked={prefs.cc_owner} onCheckedChange={(v) => setPrefs({ ...prefs, cc_owner: v })} />
          </div>
          {prefs.cc_owner && (
            <div>
              <Label>La tua email</Label>
              <Input data-testid="reminder-owner-email" value={prefs.owner_email || ""} onChange={(e) => setPrefs({ ...prefs, owner_email: e.target.value })} placeholder="tu@dominio.it" />
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button data-testid="reminder-save" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Salva
            </Button>
            <Button data-testid="reminder-test" onClick={test} disabled={testing} variant="outline">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Invia email di test
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarSync className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Sincronizzazione Google Calendar</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          Su Google Calendar → Impostazioni → seleziona il tuo calendario → <b>Indirizzo segreto in formato iCal</b>. Copia l'URL (inizia con https://calendar.google.com/…/basic.ics) e incollalo qui. PlanOp lo leggerà ogni giorno alle 08:00 (Italia) per bloccare quelle date nel planning.
        </p>
        <div className="space-y-4">
          <div>
            <Label>URL iCal privato</Label>
            <Input
              data-testid="gcal-url-input"
              value={gcal.ics_url || ""}
              onChange={(e) => setGcal({ ...gcal, ics_url: e.target.value })}
              placeholder="https://calendar.google.com/calendar/ical/…/private-…/basic.ics"
              className="font-mono text-xs"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-900">Sync automatica attiva</div>
              <div className="text-xs text-slate-500">Esegue ogni giorno alle 08:00 (Europe/Rome)</div>
            </div>
            <Switch data-testid="gcal-enabled" checked={!!gcal.enabled} onCheckedChange={(v) => setGcal({ ...gcal, enabled: v })} />
          </div>
          {gcal.last_sync && (
            <div className="text-xs text-slate-500 bg-emerald-50 border border-emerald-200 rounded p-2">
              Ultima sync: <b>{new Date(gcal.last_sync).toLocaleString("it-IT")}</b> · <b>{gcal.last_count}</b> eventi importati
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="gcal-save" onClick={saveGcal} disabled={gcalSaving} className="bg-slate-900 hover:bg-slate-800 text-white">
              {gcalSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Salva
            </Button>
            <Button data-testid="gcal-sync-now" onClick={syncNow} disabled={gcalSyncing || !gcal.ics_url} variant="outline">
              {gcalSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sincronizza ora (ICS → App)
            </Button>
            {gcalConn?.connected && !gcalConn?.invalid && (
              <>
                <Button data-testid="gcal-push-missing" onClick={pushMissing} disabled={gcalSyncing} variant="outline"
                        className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                  {gcalSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarSync className="w-4 h-4 mr-2" />}
                  Push interventi mancanti su GCal
                </Button>
                <Button data-testid="gcal-resync-all" onClick={resyncAll} disabled={gcalSyncing} variant="outline"
                        className="border-blue-300 text-blue-800 hover:bg-blue-50">
                  {gcalSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Aggiorna titoli su GCal
                </Button>
                <Button data-testid="gcal-cleanup-orphans" onClick={cleanupOrphans} disabled={gcalSyncing} variant="outline"
                        className="border-red-300 text-red-800 hover:bg-red-50">
                  {gcalSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Pulisci orfani GCal
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Dati emittente per FatturaPA / Invoicex</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Necessari per generare l'XML FatturaPA importabile in Invoicex e per il tuo Sistema di Interscambio (SdI).
        </p>
        <div className="space-y-3">
          <div>
            <Label>Denominazione (o Nome Cognome)</Label>
            <Input data-testid="issuer-denominazione" value={issuer.denominazione || ""} onChange={(e) => setIssuerState({ ...issuer, denominazione: e.target.value })} placeholder="Es. Enrico Lucchese o Studio Lucchese SRL" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>P.IVA</Label>
              <Input data-testid="issuer-piva" value={issuer.piva || ""} onChange={(e) => setIssuerState({ ...issuer, piva: e.target.value.replace(/\s/g, "") })} />
            </div>
            <div>
              <Label>Codice Fiscale</Label>
              <Input data-testid="issuer-cf" value={issuer.codice_fiscale || ""} onChange={(e) => setIssuerState({ ...issuer, codice_fiscale: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Regime fiscale</Label>
              <Select value={issuer.regime_fiscale || "RF01"} onValueChange={(v) => setIssuerState({ ...issuer, regime_fiscale: v })}>
                <SelectTrigger data-testid="issuer-regime" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="RF01">RF01 - Ordinario</SelectItem>
                  <SelectItem value="RF19">RF19 - Forfettario</SelectItem>
                  <SelectItem value="RF02">RF02 - Contribuenti minimi</SelectItem>
                  <SelectItem value="RF04">RF04 - Agricoltura</SelectItem>
                  <SelectItem value="RF18">RF18 - Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Codice ATECO</Label>
              <Input data-testid="issuer-ateco" value={issuer.codice_ateco || ""} onChange={(e) => setIssuerState({ ...issuer, codice_ateco: e.target.value })} placeholder="es. 70.22.09" />
            </div>
          </div>
          <div>
            <Label>Indirizzo</Label>
            <Input data-testid="issuer-address" value={issuer.address || ""} onChange={(e) => setIssuerState({ ...issuer, address: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>CAP</Label>
              <Input data-testid="issuer-cap" maxLength={5} value={issuer.cap || ""} onChange={(e) => setIssuerState({ ...issuer, cap: e.target.value })} />
            </div>
            <div>
              <Label>Città</Label>
              <Input data-testid="issuer-city" value={issuer.city || ""} onChange={(e) => setIssuerState({ ...issuer, city: e.target.value })} />
            </div>
            <div>
              <Label>Provincia</Label>
              <Input data-testid="issuer-provincia" maxLength={2} value={issuer.provincia || ""} onChange={(e) => setIssuerState({ ...issuer, provincia: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-700" />
                <Label className="text-emerald-900 m-0">Coordinate sede (per pianificazione per zona)</Label>
              </div>
              <Button
                data-testid="issuer-geocode"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const r = await geocodeIssuer();
                    setIssuerState({ ...issuer, base_lat: r.base_lat, base_lng: r.base_lng });
                    toast.success(`Coordinate aggiornate: ${r.base_lat.toFixed(5)}, ${r.base_lng.toFixed(5)}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Errore geocodifica");
                  }
                }}
                className="h-8 text-xs"
              >
                <MapPin className="w-3.5 h-3.5 mr-1" /> Geocodifica da indirizzo
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-emerald-800">Latitudine</Label>
                <Input
                  data-testid="issuer-base-lat"
                  type="number"
                  step="0.00001"
                  value={issuer.base_lat ?? ""}
                  onChange={(e) => setIssuerState({ ...issuer, base_lat: e.target.value === "" ? null : Number(e.target.value) })}
                  className="bg-white"
                  placeholder="es. 45.4642"
                />
              </div>
              <div>
                <Label className="text-xs text-emerald-800">Longitudine</Label>
                <Input
                  data-testid="issuer-base-lng"
                  type="number"
                  step="0.00001"
                  value={issuer.base_lng ?? ""}
                  onChange={(e) => setIssuerState({ ...issuer, base_lng: e.target.value === "" ? null : Number(e.target.value) })}
                  className="bg-white"
                  placeholder="es. 9.1900"
                />
              </div>
            </div>
            <div className="text-[11px] text-emerald-700">Usate dalla pianificazione "per zona geografica" per filtrare i clienti entro un raggio dalla sede.</div>
          </div>
          <Button data-testid="issuer-save" onClick={saveIssuer} disabled={issuerSaving} className="bg-slate-900 hover:bg-slate-800 text-white">
            {issuerSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Salva dati emittente
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarSync className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Google Calendar (sync bidirezionale)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          Quando connesso, ogni intervento creato/modificato/cancellato in PlanOp viene automaticamente pubblicato sul tuo Google Calendar (calendario primario). Titolo, indirizzo cliente, orario custom e giornata (g N/M) inclusi.
        </p>
        {gcalConn?.connected && gcalConn?.invalid && (
          <div data-testid="gcal-token-invalid-banner" className="mb-3 p-4 bg-red-50 border-2 border-red-300 rounded-md">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-red-800 text-base">Token Google scaduto — riconnessione necessaria</div>
                <div className="text-sm text-red-700 mt-1 leading-relaxed">
                  Google ha invalidato il refresh token (in modalità Testing scade ogni 7 giorni). Gli interventi non vengono più sincronizzati su Google Calendar. Clicca il bottone qui sotto per riconnettere in un click.
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <Button
                data-testid="gcal-reconnect"
                onClick={connectGcal}
                className="bg-red-700 hover:bg-red-800 text-white h-11 px-6 text-base font-semibold flex-1 sm:flex-initial"
              >
                <RefreshCw className="w-5 h-5 mr-2" /> Riconnetti Google Calendar
              </Button>
              <a
                data-testid="gcal-reconnect-link"
                href="#"
                onClick={(e) => { e.preventDefault(); connectGcal(); }}
                className="text-xs text-red-700 underline hover:text-red-900 self-center"
              >
                (in alternativa clicca qui)
              </a>
            </div>
            {gcalConn.last_error && (
              <details className="mt-3">
                <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800">Dettagli errore tecnico</summary>
                <div className="text-[10px] text-red-600 font-mono mt-1 break-all">{gcalConn.last_error}</div>
              </details>
            )}
          </div>
        )}
        {gcalConn?.connected && !gcalConn?.invalid ? (
          <div className="flex items-center justify-between gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex-wrap">
            <div className="text-sm">
              <div className="flex items-center gap-1.5 font-semibold text-emerald-800">
                <CheckCircle2 className="w-4 h-4" /> Connesso
              </div>
              <div className="text-xs text-emerald-700 mt-0.5">
                Attivo dal {(gcalConn.connected_at || "").slice(0, 10)}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid="gcal-force-reconnect"
                onClick={connectGcal}
                className="bg-red-700 hover:bg-red-800 text-white h-10"
                title="Forza una nuova autorizzazione Google. Da usare se gli appuntamenti non si sincronizzano piu' su Google Calendar."
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Riconnetti Google Calendar
              </Button>
              <Button data-testid="gcal-disconnect" variant="outline" onClick={disconnectGcal} className="text-red-700 border-red-200 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" /> Disconnetti
              </Button>
            </div>
            <div className="w-full text-[11px] text-emerald-700 leading-relaxed mt-1">
              <b>La sync non funziona?</b> Google invalida il token ogni 7 giorni finché l'app resta in modalità Testing. Clicca <b>Riconnetti Google Calendar</b> qui sopra per ripristinare la sincronizzazione (alla riconnessione PlanOp elimina i duplicati e ricrea gli appuntamenti mancanti).
            </div>
          </div>
        ) : gcalConn?.connected && gcalConn?.invalid ? null : (
          <Button data-testid="gcal-connect" onClick={connectGcal} className="bg-blue-700 hover:bg-blue-800 text-white">
            <Link2 className="w-4 h-4 mr-2" /> Connetti Google Calendar
          </Button>
        )}
      </Card>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Chiavi API personali (Desktop / Self-hosted)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          Nella build desktop di PlanOp puoi inserire qui le <b>tue</b> chiavi Anthropic e Resend. Se impostate, l'app le usa al posto della chiave Emergent condivisa. Lascia il campo vuoto per non modificare quello già salvato. Costo indicativo mensile: Anthropic ~$0,50-2, Resend gratis fino a 3000 email/mese.
        </p>
        <div className="space-y-4">
          <div>
            <Label>Chiave Anthropic (Claude Haiku 4.5)</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  data-testid="anthropic-key-input"
                  type={showAnthropic ? "text" : "password"}
                  value={apiKeysDraft.anthropic_api_key}
                  onChange={(e) => setApiKeysDraft({ ...apiKeysDraft, anthropic_api_key: e.target.value })}
                  placeholder={apiKeys.has_anthropic ? apiKeys.anthropic_api_key : "sk-ant-…"}
                  className="font-mono text-xs pr-10"
                />
                <button type="button" onClick={() => setShowAnthropic((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" data-testid="anthropic-key-toggle">
                  {showAnthropic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {apiKeys.has_anthropic && (
                <Button data-testid="anthropic-key-clear" variant="outline" onClick={() => clearOneKey("anthropic_api_key")} title="Rimuovi chiave Anthropic personale">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Ottienila su <a className="text-blue-700 underline" href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">console.anthropic.com</a>.
            </div>
          </div>

          <div>
            <Label>Chiave Resend (email)</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  data-testid="resend-key-input"
                  type={showResend ? "text" : "password"}
                  value={apiKeysDraft.resend_api_key}
                  onChange={(e) => setApiKeysDraft({ ...apiKeysDraft, resend_api_key: e.target.value })}
                  placeholder={apiKeys.has_resend ? apiKeys.resend_api_key : "re_…"}
                  className="font-mono text-xs pr-10"
                />
                <button type="button" onClick={() => setShowResend((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" data-testid="resend-key-toggle">
                  {showResend ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {apiKeys.has_resend && (
                <Button data-testid="resend-key-clear" variant="outline" onClick={() => clearOneKey("resend_api_key")} title="Rimuovi chiave Resend personale">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Ottienila su <a className="text-blue-700 underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a>. Serve verificare il dominio del mittente qui sotto.
            </div>
          </div>

          <div>
            <Label>Indirizzo "mittente" per Resend (dominio verificato)</Label>
            <Input
              data-testid="resend-from-input"
              value={apiKeysDraft.resend_from_email}
              onChange={(e) => setApiKeysDraft({ ...apiKeysDraft, resend_from_email: e.target.value })}
              placeholder="es. noreply@tuodominio.it — vuoto = usa onboarding@resend.dev"
            />
          </div>

          <Button data-testid="api-keys-save" onClick={saveApiKeys} disabled={apiKeysSaving} className="bg-slate-900 hover:bg-slate-800 text-white">
            {apiKeysSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Salva chiavi
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Archive className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Backup automatico</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          Ogni <b>domenica alle 22:00</b> ricevi via email uno zip con <code>clients.csv</code>, <code>contracts.csv</code>, <code>interventions.csv</code>. Conservalo in un posto sicuro: se un giorno perdi accesso all'app, puoi reimportare tutto in un istante.
        </p>
        <a href={weeklyBackupUrl()} target="_blank" rel="noreferrer">
          <Button data-testid="weekly-backup-download" variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Scarica backup adesso
          </Button>
        </a>
      </Card>

      <Card className="p-6 border-slate-200 bg-white mb-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-blue-700" />
          <h2 className="font-bold font-display text-lg text-slate-900">Guida operativa</h2>
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 font-mono">Rev. 1.0.1</span>
        </div>
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          Manuale PDF <b>~10 pagine</b> pensato per la segreteria: login, dashboard, clienti &amp; contratti, generazione del planning, calendario con drag&amp;drop, email di conferma, backup e FAQ. Stampalo o inviaglielo al primo giorno.
        </p>
        <a href={guideSegreteriaUrl()} target="_blank" rel="noreferrer">
          <Button data-testid="guide-segreteria-download" variant="outline" className="border-blue-200 text-blue-800 hover:bg-blue-50">
            <Download className="w-4 h-4 mr-2" />
            Scarica guida segreteria (PDF)
          </Button>
        </a>
      </Card>

      <ConsultantsBlock />

      <Card className="p-6 border-slate-200 bg-white">
        <h2 className="font-bold font-display text-lg text-slate-900 mb-3">Ultimi reminder inviati</h2>
        {log.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Nessun reminder ancora inviato.</div>
        ) : (
          <div className="space-y-2 text-sm">
            {log.slice(0, 20).map((r, i) => (
              <div key={i} className="flex items-center justify-between p-2 border-b border-slate-100">
                <div>
                  <span className="font-mono text-xs">{r.date}</span> · <span>{r.recipient}</span>
                </div>
                <span className="text-xs text-slate-400 font-mono">{new Date(r.sent_at).toLocaleString("it-IT")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
